import { memo } from "react";
import type WaveformData from "waveform-data";

const scaleY = (amplitude: number, height: number) => {
  const range = 256;
  const offset = 128;
  return height - ((amplitude + offset) * height) / range;
};

const Waveform: React.FC<{
  data: WaveformData;
  color: string;
}> = ({ data, color }) => {
  return (
    <canvas
      className="h-full"
      ref={(canvas) => {
        if (!canvas) {
          return;
        }

        const closestRelative = canvas.closest(".relative");
        if (!closestRelative) {
          return;
        }

        const drawWaveform = () => {
          // set canvas size, and account for high DPI displays
          canvas.width = closestRelative.clientWidth * 2;
          canvas.height = closestRelative.clientHeight * 2;

          const ctx = canvas.getContext("2d");
          if (!ctx) {
            return;
          }

          ctx.clearRect(0, 0, canvas.width, canvas.height); // clear canvas
          ctx.beginPath();

          const channel = data.channel(0);
          const scaleX = canvas.width / data.length;

          // loop forwards, drawing the upper half of the waveform
          for (let i = 0; i < data.length; i++) {
            const val = channel.max_sample(i);

            const x = i * scaleX;
            ctx.lineTo(x + 0.5, scaleY(val, canvas.height) + 0.5);
          }

          // loop backwards, drawing the lower half of the waveform
          for (let i = data.length - 1; i >= 0; i--) {
            const val = channel.min_sample(i);

            const x = i * scaleX;
            ctx.lineTo(x + 0.5, scaleY(val, canvas.height) + 0.5);
          }

          ctx.closePath();

          ctx.strokeStyle = color;
          ctx.fillStyle = color;

          ctx.stroke();
          ctx.fill();
        };

        drawWaveform();

        const observer = new ResizeObserver(drawWaveform);
        observer.observe(closestRelative);
        return () => {
          observer.disconnect();
        };
      }}
    />
  );
};

export default memo(Waveform);
